import {observer} from "mobx-react";
import {useRef, useState} from "react";
import {Typography, Chip, alpha, Button} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import SchoolIcon from "@mui/icons-material/School";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {Frame} from "../common/Frame";
import {VideoPlayer} from "../video/VideoPlayer";
import {lessons, recording, recordings} from "../../proto/compiled";
import {AppRoutes} from "../../AppRoutes";

interface VideoExampleCardProps {
  videoExample: lessons.IVideoExample;
}

const formatTimestamp = (ms?: number | null) => {
  if (ms === undefined || ms === null || ms < 0) {
    return "—";
  }
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }
  return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

const formatDuration = (startMs?: number | null, endMs?: number | null) => {
  if (!startMs || !endMs || endMs <= startMs) return "—";
  const durationMs = endMs - startMs;
  const totalSeconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) {
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }
  return `${seconds}s`;
};

const getParticipantIcon = (role?: recordings.RecordingParticipantRole | null) => {
  switch (role) {
    case recordings.RecordingParticipantRole.rpr_instructor:
      return <SchoolIcon fontSize="small" />;
    case recordings.RecordingParticipantRole.rpr_supervisor:
      return <SupervisorAccountIcon fontSize="small" />;
    default:
      return <PersonIcon fontSize="small" />;
  }
};

export const VideoExampleCard = observer(({videoExample}: VideoExampleCardProps) => {
  const theme = useTheme();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [aspectRatio, setAspectRatio] = useState(16 / 9);

  const annotation = videoExample.annotation;
  const isGoodPractice = annotation?.annotationType === recording.AnnotationType.AT_GOOD_PRACTICE;
  const isCommonMistake = annotation?.annotationType === recording.AnnotationType.AT_COMMON_MISTAKE;

  const badgeColor = isGoodPractice
    ? theme.palette.success.main
    : isCommonMistake
      ? theme.palette.error.main
      : theme.palette.info.main;

  const badgeText = isGoodPractice
    ? "GOOD PRACTICE"
    : isCommonMistake
      ? "COMMON MISTAKE"
      : "VIDEO EXAMPLE";

  const BadgeIcon = isGoodPractice ? CheckIcon : isCommonMistake ? CloseIcon : null;

  return (
    <Frame
      row
      gap={2}
      padding={2}
      radius={1.5}
      style={{
        border: `2px solid ${badgeColor}`,
        backgroundColor: alpha(badgeColor, 0.04),
      }}
    >
      {/* Video Player - 1/2 width */}
      <Frame
        direction="column"
        style={{
          width: '50%',
          flexShrink: 0,
        }}
      >
        {videoExample.videoUrl && annotation && (
          <VideoPlayer
            videoRef={videoRef}
            src={videoExample.videoUrl}
            annotations={[annotation as recording.IRecordingAnnotation]}
            selectedAnnotation={annotation as recording.IRecordingAnnotation}
            limitToAnnotation={annotation as recording.IRecordingAnnotation}
            initialTimeMs={annotation.startMs ?? 0}
            sizing="contain"
            aspectRatio={aspectRatio}
            onAspectRatioChange={setAspectRatio}
          />
        )}
      </Frame>

      {/* Content - 2/3 width */}
      <Frame direction="column" gap={1} grow>
        {/* Header with badge and time */}
        <Frame row justifyContent="space-between" alignItems="flex-start" gap={2}>
          <Frame direction="column" gap={0.5} grow>
            <Frame row gap={0.5} alignItems="center">
              {BadgeIcon && (
                <BadgeIcon
                  fontSize="small"
                  style={{color: badgeColor}}
                />
              )}
              <Typography
                variant="caption"
                fontWeight="bold"
                style={{color: badgeColor, letterSpacing: '0.5px'}}
              >
                {badgeText}
              </Typography>
            </Frame>
            <Typography variant="subtitle1" fontWeight="bold">
              {annotation?.label}
            </Typography>
          </Frame>

          <Typography variant="body2" color="textSecondary" style={{whiteSpace: 'nowrap'}}>
            {formatTimestamp(annotation?.startMs)}{annotation?.endMs ? ` → ${formatTimestamp(annotation?.endMs)}` : ''}
          </Typography>
        </Frame>

        {/* Description */}
        {annotation?.description && (
          <Typography
            variant="body2"
            color="textSecondary"
            style={{
              lineHeight: 1.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {annotation.description}
          </Typography>
        )}

        {/* Footer with participants and open session button */}
        <Frame row justifyContent="space-between" alignItems="flex-end" gap={1} style={{marginTop: 'auto'}}>
          <Frame row gap={1} wrap style={{flex: '1 1 0', minWidth: 0}}>
            {videoExample.participants?.map((participant, idx) => (
              <Chip
                key={idx}
                icon={getParticipantIcon(participant.role)}
                label={participant.name}
                size="small"
                variant="outlined"
              />
            ))}
          </Frame>

          {videoExample.recordingId && (
            <Button
              variant="outlined"
              size="small"
              endIcon={<OpenInNewIcon />}
              href={AppRoutes.organizationSessionPath(videoExample.recordingId)}
              target="_blank"
              style={{flexShrink: 0}}
            >
              Open Session
            </Button>
          )}
        </Frame>
      </Frame>
    </Frame>
  );
});
